// document-scanner.js - 코너 디텍션 + 원근 보정

import { detectCornersWithDocAligner, isDocAlignerReady } from './docaligner-inference.js';

/**
 * 이미지에서 문서 코너 감지
 * DocAligner (딥러닝) 우선 시도, 실패 시 OpenCV fallback
 * @param {string} imageDataURL
 * @returns {Promise<{tl: {x,y}, tr: {x,y}, br: {x,y}, bl: {x,y}}>}
 */
export async function detectCorners(imageDataURL) {
  // DocAligner 우선 시도
  if (isDocAlignerReady()) {
    try {
      const corners = await detectCornersWithDocAligner(imageDataURL);
      if (corners) {
        console.log('DocAligner: corners detected successfully');
        return corners;
      }
      console.log('DocAligner: low confidence, fallback to OpenCV');
    } catch (e) {
      console.warn('DocAligner failed, fallback to OpenCV:', e);
    }
  }

  // OpenCV fallback
  return detectCornersWithOpenCV(imageDataURL);
}

/**
 * OpenCV 기반 코너 감지 (기존 로직)
 * @param {string} imageDataURL
 * @returns {Promise<{tl: {x,y}, tr: {x,y}, br: {x,y}, bl: {x,y}}>}
 */
async function detectCornersWithOpenCV(imageDataURL) {
  const img = await loadImage(imageDataURL);

  // offscreen canvas에 이미지 그리기
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let src = null;
  let gray = null;
  let blurred = null;
  let edges = null;
  let dilated = null;
  let contours = null;
  let hierarchy = null;
  let kernel = null;

  try {
    src = cv.matFromImageData(imageData);
    gray = new cv.Mat();
    blurred = new cv.Mat();
    edges = new cv.Mat();
    dilated = new cv.Mat();
    contours = new cv.MatVector();
    hierarchy = new cv.Mat();

    // 그레이스케일 변환
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

    // 가우시안 블러
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0);

    // Canny 에지 검출
    cv.Canny(blurred, edges, 50, 150);

    // Dilate (간격 연결)
    kernel = cv.Mat.ones(3, 3, cv.CV_8U);
    cv.dilate(edges, dilated, kernel);

    // 컨투어 찾기
    cv.findContours(dilated, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

    // 면적 순 정렬
    const contourAreas = [];
    for (let i = 0; i < contours.size(); i++) {
      contourAreas.push({
        index: i,
        area: cv.contourArea(contours.get(i))
      });
    }
    contourAreas.sort((a, b) => b.area - a.area);

    const imgArea = img.width * img.height;

    // 4꼭짓점 폴리곤 찾기
    for (const { index } of contourAreas) {
      const contour = contours.get(index);
      const peri = cv.arcLength(contour, true);
      const approx = new cv.Mat();

      cv.approxPolyDP(contour, approx, 0.02 * peri, true);

      if (approx.rows === 4) {
        const area = cv.contourArea(approx);
        // 이미지 면적의 10% 이상이어야 문서로 인정
        if (area > imgArea * 0.1) {
          const points = matToPoints(approx);
          approx.delete();
          const sorted = sortCorners(points, img.width, img.height);
          return sorted;
        }
      }

      approx.delete();
    }

    // 문서 미감지 → 기본 코너 (10% 인셋)
    return getDefaultCorners(img.width, img.height);

  } finally {
    // 메모리 해제
    if (src) src.delete();
    if (gray) gray.delete();
    if (blurred) blurred.delete();
    if (edges) edges.delete();
    if (dilated) dilated.delete();
    if (contours) contours.delete();
    if (hierarchy) hierarchy.delete();
    if (kernel) kernel.delete();
  }
}

/**
 * 코너로부터 출력 크기 계산 (문서의 실제 비율 유지)
 * @param {{tl: {x,y}, tr: {x,y}, br: {x,y}, bl: {x,y}}} corners
 * @param {number} maxDimension - 최대 출력 크기 (기본 1200px)
 * @returns {{width: number, height: number}}
 */
export function calculateOutputSizeFromCorners(corners, maxDimension = 1200) {
  // Calculate edge lengths
  const topWidth = Math.sqrt(
    Math.pow(corners.tr.x - corners.tl.x, 2) + Math.pow(corners.tr.y - corners.tl.y, 2)
  );
  const bottomWidth = Math.sqrt(
    Math.pow(corners.br.x - corners.bl.x, 2) + Math.pow(corners.br.y - corners.bl.y, 2)
  );
  const leftHeight = Math.sqrt(
    Math.pow(corners.bl.x - corners.tl.x, 2) + Math.pow(corners.bl.y - corners.tl.y, 2)
  );
  const rightHeight = Math.sqrt(
    Math.pow(corners.br.x - corners.tr.x, 2) + Math.pow(corners.br.y - corners.tr.y, 2)
  );

  // Average width and height
  const avgWidth = (topWidth + bottomWidth) / 2;
  const avgHeight = (leftHeight + rightHeight) / 2;

  // Calculate aspect ratio
  const aspectRatio = avgWidth / avgHeight;

  // Scale to fit within maxDimension
  let outWidth, outHeight;
  if (aspectRatio >= 1) {
    // Wider than tall
    outWidth = maxDimension;
    outHeight = Math.round(maxDimension / aspectRatio);
  } else {
    // Taller than wide
    outHeight = maxDimension;
    outWidth = Math.round(maxDimension * aspectRatio);
  }

  return { width: outWidth, height: outHeight };
}

/**
 * 원근 보정 적용
 * @param {string} imageDataURL
 * @param {{tl: {x,y}, tr: {x,y}, br: {x,y}, bl: {x,y}}} corners
 * @param {{width: number, height: number}} [outputSize] - 출력 크기 (미지정시 코너에서 자동 계산)
 * @returns {Promise<string>} 보정된 이미지 dataURL
 */
export async function applyPerspectiveCorrection(imageDataURL, corners, outputSize) {
  const img = await loadImage(imageDataURL);

  // If outputSize not provided, calculate from corners
  if (!outputSize) {
    outputSize = calculateOutputSizeFromCorners(corners, 1200);
  }

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let src = null;
  let dst = null;
  let srcPoints = null;
  let dstPoints = null;
  let M = null;

  try {
    src = cv.matFromImageData(imageData);

    const outW = outputSize.width;
    const outH = outputSize.height;

    // 소스 4점
    srcPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
      corners.tl.x, corners.tl.y,
      corners.tr.x, corners.tr.y,
      corners.br.x, corners.br.y,
      corners.bl.x, corners.bl.y
    ]);

    // 목표 직사각형 4점
    dstPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
      0, 0,
      outW, 0,
      outW, outH,
      0, outH
    ]);

    // 원근 변환 행렬
    M = cv.getPerspectiveTransform(srcPoints, dstPoints);

    // 변환 적용
    dst = new cv.Mat();
    cv.warpPerspective(src, dst, M, new cv.Size(outW, outH));

    // 결과를 canvas → dataURL
    const outCanvas = document.createElement('canvas');
    outCanvas.width = outW;
    outCanvas.height = outH;
    cv.imshow(outCanvas, dst);

    return outCanvas.toDataURL('image/jpeg', 0.92);

  } catch (e) {
    // 원근 보정 실패 시 원본 반환
    console.error('Perspective correction failed:', e);
    return imageDataURL;

  } finally {
    if (src) src.delete();
    if (dst) dst.delete();
    if (srcPoints) srcPoints.delete();
    if (dstPoints) dstPoints.delete();
    if (M) M.delete();
  }
}

/**
 * cv.Mat → 좌표 배열 변환
 */
function matToPoints(mat) {
  const points = [];
  for (let i = 0; i < mat.rows; i++) {
    points.push({
      x: mat.intAt(i, 0),
      y: mat.intAt(i, 1)
    });
  }
  return points;
}

/**
 * 4개 좌표를 TL/TR/BR/BL 순서로 정렬
 */
function sortCorners(points, imgWidth, imgHeight) {
  // 중심점
  const cx = points.reduce((s, p) => s + p.x, 0) / 4;
  const cy = points.reduce((s, p) => s + p.y, 0) / 4;

  const tl = points.find(p => p.x < cx && p.y < cy)
    || points.reduce((best, p) => (p.x + p.y < best.x + best.y ? p : best));
  const tr = points.find(p => p.x >= cx && p.y < cy)
    || points.reduce((best, p) => (p.x - p.y > best.x - best.y ? p : best));
  const br = points.find(p => p.x >= cx && p.y >= cy)
    || points.reduce((best, p) => (p.x + p.y > best.x + best.y ? p : best));
  const bl = points.find(p => p.x < cx && p.y >= cy)
    || points.reduce((best, p) => (p.y - p.x > best.y - best.x ? p : best));

  return { tl, tr, br, bl };
}

/**
 * 기본 코너 (10% 인셋)
 */
function getDefaultCorners(imgWidth, imgHeight) {
  const insetX = Math.round(imgWidth * 0.1);
  const insetY = Math.round(imgHeight * 0.1);
  return {
    tl: { x: insetX, y: insetY },
    tr: { x: imgWidth - insetX, y: insetY },
    br: { x: imgWidth - insetX, y: imgHeight - insetY },
    bl: { x: insetX, y: imgHeight - insetY }
  };
}

/**
 * 이미지 로드 헬퍼
 */
function loadImage(dataURL) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('이미지 로드 실패'));
    img.src = dataURL;
  });
}
